import {createStyles, Group, Loader, Stack, Stepper, Text, Tooltip} from '@mantine/core';
import React, {useReducer, useMemo, useCallback} from 'react';

import {IGNORE_FIELD_ID} from 'spreadsheet-import-wizard/shared/ColumnMapper';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import type {RunModalProps} from 'ui-library/runModal';

import ColumnMappingPane from './ColumnMappingPane';
import ConfigurePane from './ConfigurePane';
import FileUploadPane from './FileUploadPane';
import FinalizeImportPane from './FinalizeImportPane';
import {MOLECULE_BUILT_IN_FIELDS} from './MoleculeFieldConstants';
import moleculeImportReducer, {INITIAL_STATE, ModalStep} from './moleculeImportReducer';
import type {ModalState} from './moleculeImportReducer';
import useSubmitImport from './useSubmitImport';

const useStyles = createStyles(() => ({
  container: {
    flex: 1,
    overflow: 'hidden',
  },
  stepContent: {
    flex: 1,
    overflow: 'auto',
  },
}));

const STEPPER_STEPS: Array<{key: ModalStep; label: string}> = [
  {key: 'CONFIGURE', label: 'Select Schema'},
  {key: 'FILE_UPLOAD', label: 'Upload File'},
  {key: 'MAP_COLUMNS', label: 'Map Columns'},
  {key: 'FINALIZE_IMPORT', label: 'Finalize Import'},
];

interface ModalFooterProps {
  currentStepIndex: number;
  step: ModalStep;
  isNextDisabled: boolean;
  isImportDisabled: boolean;
  importDisabledTooltip: JSX.Element | null;
  onBack: () => void;
  onNext: () => void;
  onCancel: () => void;
  onImport: () => void;
}

function ModalFooter({
  currentStepIndex,
  step,
  isNextDisabled,
  isImportDisabled,
  importDisabledTooltip,
  onBack,
  onNext,
  onCancel,
  onImport,
}: ModalFooterProps): JSX.Element {
  if (step === 'SUBMITTING') {
    return <Group position="apart" />;
  }

  return (
    <Group position="apart">
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        size={ButtonSize.MEDIUM}
        onClick={currentStepIndex === 0 ? onCancel : onBack}
      >
        {currentStepIndex === 0 ? 'Cancel' : 'Back'}
      </Button>
      {step === 'FINALIZE_IMPORT' ? (
        <Tooltip
          label={importDisabledTooltip}
          disabled={!isImportDisabled || importDisabledTooltip == null}
          openDelay={0}
          closeDelay={0}
        >
          <span>
            <Button
              buttonStyle={ButtonStyle.PRIMARY}
              size={ButtonSize.MEDIUM}
              disabled={isImportDisabled}
              onClick={onImport}
            >
              Import
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.MEDIUM}
          onClick={onNext}
          disabled={isNextDisabled}
        >
          Next
        </Button>
      )}
    </Group>
  );
}

interface MoleculeImportModalProps extends RunModalProps<void> {
  initialFolderId?: string | null;
}

/** Returns true when all required built-in mappings are selected. */
function hasRequiredColumnMappings(columnMappings: Record<string, string>): boolean {
  const selectedFields = new Set(
    Object.values(columnMappings).filter(
      (fieldId): fieldId is string => fieldId != null && fieldId !== '' && fieldId !== IGNORE_FIELD_ID
    )
  );

  const hasNameMapping = selectedFields.has(MOLECULE_BUILT_IN_FIELDS.NAME);
  const hasStructureMapping =
    selectedFields.has(MOLECULE_BUILT_IN_FIELDS.STRUCTURE_SMILES) ||
    selectedFields.has(MOLECULE_BUILT_IN_FIELDS.STRUCTURE_MOLFILE);

  return hasNameMapping && hasStructureMapping;
}

function getImportDisabledReasons(state: ModalState): Array<string> {
  const reasons: Array<string> = [];
  if (!state.schemaId) {
    reasons.push('Select a schema to enable Import');
  }
  if (!hasRequiredColumnMappings(state.columnMappings)) {
    reasons.push('Map Molecule name and structure columns to enable Import');
  }
  if (state.registrationConfig == null) {
    reasons.push('Complete registration configuration to enable Import');
    return reasons;
  }
  if (!state.registrationConfig.registryId) {
    reasons.push('Select a registry to enable Import');
  }
  if (!state.registrationConfig.folderId) {
    reasons.push('Select a project folder to enable Import');
  }
  if (!state.registrationConfig.namingStrategy) {
    reasons.push('Select a naming strategy to enable Import');
  }
  return reasons;
}

function getImportDisabledTooltip(reasons: Array<string>): JSX.Element | null {
  if (reasons.length === 0) {
    return null;
  }
  return (
    <Stack spacing={4}>
      {reasons.map((reason) => (
        <Text key={reason} size="xs">
          {reason}
        </Text>
      ))}
    </Stack>
  );
}

export default function MoleculeImportModal({close, initialFolderId}: MoleculeImportModalProps): JSX.Element {
  const {classes} = useStyles();
  const [state, dispatch] = useReducer(moleculeImportReducer, {
    ...INITIAL_STATE,
    initialFolderId: initialFolderId ?? null,
  });

  const {handleSubmit, isSubmitting} = useSubmitImport(state, dispatch, close);

  const currentStepIndex = useMemo(() => {
    const index = STEPPER_STEPS.findIndex((s) => s.key === state.step);
    return index === -1 ? STEPPER_STEPS.length : index;
  }, [state.step]);

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      dispatch({type: 'GO_TO_STEP', payload: {step: STEPPER_STEPS[currentStepIndex - 1].key}});
    }
  }, [currentStepIndex]);

  const handleNext = useCallback(() => {
    if (currentStepIndex < STEPPER_STEPS.length - 1) {
      dispatch({type: 'GO_TO_STEP', payload: {step: STEPPER_STEPS[currentStepIndex + 1].key}});
    }
  }, [currentStepIndex]);

  const isNextDisabled = useMemo(() => {
    switch (state.step) {
      case 'CONFIGURE':
        return !state.schemaId;
      case 'FILE_UPLOAD':
        return state.headers == null;
      case 'MAP_COLUMNS':
        return !hasRequiredColumnMappings(state.columnMappings);
      case 'FINALIZE_IMPORT':
        return false;
      default:
        return true;
    }
  }, [state.step, state.schemaId, state.headers, state.columnMappings]);

  const importDisabledReasons = useMemo(() => getImportDisabledReasons(state), [state]);
  const isImportDisabled = isSubmitting || importDisabledReasons.length > 0;
  const importDisabledTooltip =
    isImportDisabled && !isSubmitting ? getImportDisabledTooltip(importDisabledReasons) : null;

  const renderStepContent = (): JSX.Element => {
    switch (state.step) {
      case 'CONFIGURE':
        return <ConfigurePane state={state} dispatch={dispatch} />;
      case 'FILE_UPLOAD':
        return <FileUploadPane state={state} dispatch={dispatch} />;
      case 'MAP_COLUMNS':
        return <ColumnMappingPane state={state} dispatch={dispatch} />;
      case 'FINALIZE_IMPORT':
        return <FinalizeImportPane state={state} dispatch={dispatch} />;
      case 'SUBMITTING':
        return (
          <Stack align="center" spacing="md" py="xl">
            <Loader size="lg" />
            <Text>Validating and processing...</Text>
          </Stack>
        );
      case 'DONE':
        return <Text>Done!</Text>;
      default:
        return <Text>Unknown step</Text>;
    }
  };

  return (
    <Stack spacing="lg" className={classes.container}>
      <Stepper active={currentStepIndex} allowNextStepsSelect={false} breakpoint="sm">
        {STEPPER_STEPS.map((step) => (
          <Stepper.Step key={step.key} label={step.label} />
        ))}
      </Stepper>

      <div className={classes.stepContent}>{renderStepContent()}</div>

      <ModalFooter
        currentStepIndex={currentStepIndex}
        step={state.step}
        isNextDisabled={isNextDisabled}
        isImportDisabled={isImportDisabled}
        importDisabledTooltip={importDisabledTooltip}
        onBack={handleBack}
        onNext={handleNext}
        onCancel={() => close()}
        onImport={handleSubmit}
      />
    </Stack>
  );
}
